import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import type { EmailHistory } from "@/types/database";

export type EmailPreview = {
  kind: string;
  to_email: string;
  subject: string;
  html: string;
  text: string;
  pay_url: string;
  booking_reference: string;
};

export type EmailApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; code?: string; unconfigured?: boolean };

async function invokeEmail(
  bookingId: string,
  action: "preview" | "send",
  toEmail?: string
): Promise<EmailApiResult<EmailPreview & { email_history_id?: string; provider_message_id?: string }>> {
  if (!supabase || !isSupabaseConfigured) {
    return {
      ok: false,
      error: "Supabase is not configured.",
      unconfigured: true,
    };
  }

  const { data, error } = await supabase.functions.invoke("send-booking-email", {
    body: {
      booking_id: bookingId,
      action,
      ...(toEmail ? { to_email: toEmail } : {}),
    },
  });

  const payload = data as
    | (EmailPreview & {
        ok?: boolean;
        error?: string;
        code?: string;
        email_history_id?: string;
        provider_message_id?: string;
      })
    | null;

  if (payload?.subject && payload?.html && action === "preview") {
    return { ok: true, data: payload };
  }
  if (payload?.ok || (action === "send" && payload?.email_history_id && !payload?.error)) {
    return {
      ok: true,
      data: {
        kind: "payment_request",
        to_email: payload.to_email || toEmail || "",
        subject: payload.subject || "",
        html: payload.html || "",
        text: payload.text || "",
        pay_url: payload.pay_url || "",
        booking_reference: payload.booking_reference || "",
        email_history_id: payload.email_history_id,
        provider_message_id: payload.provider_message_id,
      },
    };
  }

  const errMsg = payload?.error || error?.message || "Email request failed.";
  const code = payload?.code;
  const friendly = errMsg;

  return {
    ok: false,
    error: friendly,
    code,
  };
}

export function previewPaymentRequest(bookingId: string) {
  return invokeEmail(bookingId, "preview");
}

export function sendPaymentRequest(bookingId: string, toEmail?: string) {
  return invokeEmail(bookingId, "send", toEmail);
}

export async function listEmailHistory(
  bookingId: string
): Promise<EmailApiResult<EmailHistory[]>> {
  if (!supabase || !isSupabaseConfigured) {
    return { ok: false, error: "Supabase is not configured.", unconfigured: true };
  }
  const { data, error } = await supabase
    .from("email_history")
    .select("*")
    .eq("booking_id", bookingId)
    .order("created_at", { ascending: false });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: (data ?? []) as EmailHistory[] };
}
